import _ from 'lodash'
import React from 'react'
import BPromise from 'bluebird'
import { Icon } from '@blueprintjs/core'
import Api from 'browser/app/models/apis'
import { Query } from 'shared-libs/models/query'
import { Entity } from 'shared-libs/models/entity'
import { Button } from 'browser/components/atomic-elements/atoms/button/button'
import { Popover } from 'browser/components/atomic-elements/atoms/popover/popover'
import { Checkbox } from 'browser/components/atomic-elements/atoms/checkbox/checkbox'
import { TetherTarget } from 'browser/components/atomic-elements/atoms/tether-target'
import { getLocalStorageItems, putLocalStorageItem } from 'browser/contexts/local-storage/utils'
import { LoadingSpinner } from 'browser/components/atomic-elements/atoms/loading-spinner/loading-spinner'

export const SELECTED_CALENDARS_KEY = 'vector.appointment-calendar.selected-calendars'

interface IAppointmentCalendarSelectorProps {
  onCalendarsSelected?: (calendarIds: Record<string, boolean>) => void
}

export class AppointmentCalendarSelector extends React.PureComponent<IAppointmentCalendarSelectorProps> {
  public render() {
    return (
      <TetherTarget
        automaticAdjustOffset={true}
        isEnabled={true}
        openOnClick={true}
        openOnFocus={false}
        tethered={this.renderPopoverContent()}
      >
        <Button>
          <Icon icon="calendar" />
        </Button>
      </TetherTarget>
    )
  }

  private renderPopoverContent() {
    return (
      <Popover>
        <CalendarListContent onCalendarToggle={this.handleCalendarToggle} />
      </Popover>
    )
  }

  private handleCalendarToggle = (
    calendarId: string,
    selected: boolean,
    selectedCalendarsMap: Record<string, boolean>
  ) => {
    this.props.onCalendarsSelected?.(selectedCalendarsMap)
  }
}

interface CalendarListContentProps {
  onCalendarToggle: (calendarId: string, selected: boolean, selectedCalendarsMap: Record<string, boolean>) => void
}

interface Calendar {
  entityId: string
  displayName: string
}

interface CalendarListContentState {
  isLoading: boolean
  calendars: Calendar[]
  selectedCalendarsMap: Record<string, boolean>
}

class CalendarListContent extends React.Component<
  CalendarListContentProps,
  CalendarListContentState
> {
  private fetchPromise?: BPromise<any>

  constructor(props: CalendarListContentProps) {
    super(props)
    this.state = {
      isLoading: true,
      calendars: [],
      selectedCalendarsMap: {},
    }
  }

  public componentDidMount(): void {
    const collection = new Query(Api)
      .setEntityType('/1.0/entities/metadata/core_yms_calendar.json')
      .getCollection()

    this.fetchPromise = collection.find().then((entities: Entity[]) => {
      const calendars = _.map(entities, (entity) => ({
        entityId: entity.uniqueId,
        displayName: entity.get('core_yms_calendar.name'),
      }))
      const selectedCalendarsMap = this.createSelectedCalendarsMap(calendars)
      this.setState({ calendars, selectedCalendarsMap, isLoading: false })
    })
  }

  public componentWillUnmount(): void {
    this.fetchPromise?.cancel()
  }

  public render() {
    const { isLoading, calendars, selectedCalendarsMap } = this.state

    if (isLoading) {
      return <LoadingSpinner />
    }

    if (_.isEmpty(calendars)) {
      return <div>No calendars available</div>
    }

    return (
      <>
        <div className="fw6 mb2">Calendars</div>
        <div className="overflow-y-auto">
          {calendars.map((calendar) => (
            <div key={calendar.entityId} className="u-bumperBottom">
              <Checkbox
                label={calendar.displayName}
                value={selectedCalendarsMap[calendar.entityId] || false}
                onChange={(selected) => this.handleToggle(calendar.entityId, selected)}
              />
            </div>
          ))}
        </div>
      </>
    )
  }

  private handleToggle = (calendarId: string, selected: boolean) => {
    this.setState(
      (prevState) => ({
        selectedCalendarsMap: { ...prevState.selectedCalendarsMap, [calendarId]: selected },
      }),
      () => {
        const { selectedCalendarsMap } = this.state
        putLocalStorageItem(SELECTED_CALENDARS_KEY, selectedCalendarsMap)
        this.props.onCalendarToggle(calendarId, selected, selectedCalendarsMap)
      }
    )
  }

  private createSelectedCalendarsMap(calendars: Calendar[]): Record<string, boolean> {
    const storedValues = getLocalStorageItems()
    const savedSelections = storedValues[SELECTED_CALENDARS_KEY] as
      | Record<string, boolean>
      | undefined

    if (savedSelections) {
      return savedSelections
    }

    // default to all enabled
    const calendarIds = _.map(calendars, 'entityId')
    return _.zipObject(
      calendarIds,
      calendarIds.map(() => true)
    )
  }
}
